// Resolves the conditional-text markers in a help article against the
// platform's live settings. Plan Step B2 (docs/planning/sa-settings-doc-plan.md).
//
// WHERE IT RUNS: AFTER `sanitizeHelpHtml`, before the `v-html` sink in
// `components/Help/HelpRenderer.vue`. The server renders the markers
// (`api/src/utils/help_markdown.py`):
//
//   <div data-help-when="COND">...</div>
//   <div data-help-otherwise="COND">...</div>      (adjacent, same COND)
//   <span data-help-value="KEY">authored fallback</span>
//
// and `GET /api/help/context` supplies the values.
//
// WHY THIS CANNOT WIDEN THE SANITIZER. It only REMOVES nodes (the losing
// branch, orphans) and UNWRAPS the winning one, and it writes values as
// text nodes. It never builds markup from a value, so the sanitizer stays
// the last word on which tags and attributes exist. The input is parsed with
// DOMParser, which yields an inert document (no scripts run, nothing loads).
//
// FALLBACK RULE (design principle 5, fail toward the authored record):
// anything not positively known is UNKNOWN, and unknown resolves to the
// neutral text. A block shows its `otherwise` branch (or disappears when it
// has none, i.e. `hide-when-off`); an inline value shows its authored
// fallback. "Not known" includes: no context, a missing key, a value of the
// wrong type, a malformed condition. Negation does not turn unknown into
// true: `when not k` with `k` unknown still shows `otherwise`.
//
// KEY LOOKUPS USE A Map. The keys come from article markup and are matched
// against a context object parsed from the network; a plain-object lookup
// would resolve `constructor` / `toString` / `__proto__` to inherited members.
// Copying own entries into a Map makes every such key an ordinary missing key.

const MARKER_PREFIX = "data-help-";
const WHEN_ATTR = "data-help-when";
const OTHERWISE_ATTR = "data-help-otherwise";
const VALUE_ATTR = "data-help-value";

// Mirrors the renderer's condition grammar (`_CONDITION_RE` in
// help_markdown.py): `[not ]key[ has item]`. The key allowlist lives
// server-side only; an unknown well-formed key simply has no value here.
const CONDITION_RE = /^(not )?([a-z][a-z0-9_]*)(?: has ([a-z0-9_]+))?$/;
const VALUE_KEY_RE = /^[a-z][a-z0-9_]*$/;
// Grammar words, never keys (mirrors the sanitizers).
const RESERVED_WORDS = new Set(["not", "has"]);

function contextMap(context) {
  if (context === null || typeof context !== "object" || Array.isArray(context)) {
    return new Map();
  }
  return new Map(Object.entries(context));
}

// A list setting is either `[{slug, label}]` (the endpoint's shape) or a plain
// list of slug strings. Entries of any other shape are ignored.
function listSlugs(list) {
  const slugs = [];
  for (const entry of list) {
    if (typeof entry === "string") slugs.push(entry);
    else if (entry && typeof entry.slug === "string") slugs.push(entry.slug);
  }
  return slugs;
}

function listLabels(list) {
  const labels = [];
  for (const entry of list) {
    if (typeof entry === "string") labels.push(entry);
    else if (entry && typeof entry.label === "string" && entry.label) {
      labels.push(entry.label);
    }
  }
  return labels;
}

/**
 * Evaluate a condition: true, false, or undefined (unknown).
 *
 * A list that is empty counts as unknown, not "contains nothing": the
 * endpoint refuses to store an empty allowed-methods list, so an empty one
 * can only mean the server could not read it.
 */
function evaluateCondition(condition, values) {
  const match = CONDITION_RE.exec(condition);
  if (!match || RESERVED_WORDS.has(match[2])) return undefined;
  const [, negation, key, item] = match;
  if (!values.has(key)) return undefined;
  const value = values.get(key);

  let result;
  if (item === undefined) {
    if (typeof value !== "boolean") return undefined;
    result = value;
  } else {
    if (!Array.isArray(value) || value.length === 0) return undefined;
    result = listSlugs(value).includes(item);
  }
  return negation ? !result : result;
}

// Fee rates arrive as fractions (0.0395); prose wants "3.95%".
function formatPercent(value) {
  // A fraction: anything outside 0..1 (a percent sent by mistake, a negative)
  // is not a rate, so it is treated as absent and the authored fallback shows.
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  if (value < 0 || value > 1) return undefined;
  return `${Number((value * 100).toFixed(2))}%`;
}

// "A", "A and B", "A, B, and C".
function joinPlain(labels) {
  if (labels.length === 0) return undefined;
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

// Key-specific display formatting for inline values. Keys not listed show a
// string or finite number as-is.
const VALUE_FORMATTERS = new Map([
  ["global_platform_fee_rate", formatPercent],
  ["platform_fee_rate", formatPercent],
  [
    "allowed_decoration_methods",
    (value) => (Array.isArray(value) ? joinPlain(listLabels(value)) : undefined),
  ],
]);

function displayValue(key, value) {
  const formatter = VALUE_FORMATTERS.get(key);
  if (formatter) return formatter(value);
  if (typeof value === "string" && value) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

function unwrap(element) {
  element.replaceWith(...element.childNodes);
}

/**
 * Resolve conditional blocks and inline values in sanitized help HTML.
 *
 * @param {string} sanitizedHtml Output of `sanitizeHelpHtml`.
 * @param {object|null|undefined} context The `/api/help/context` payload, or
 *   nothing when it could not be read.
 * @returns {string} HTML with every marker resolved and removed.
 */
export function resolveHelpConditions(sanitizedHtml, context) {
  if (!sanitizedHtml || typeof sanitizedHtml !== "string") return "";
  // No markers: hand the string back untouched, so every ordinary article
  // renders byte-for-byte as before this resolver existed.
  if (!sanitizedHtml.includes(MARKER_PREFIX)) return sanitizedHtml;

  const values = contextMap(context);
  const doc = new DOMParser().parseFromString(
    `<body>${sanitizedHtml}</body>`,
    "text/html"
  );
  const body = doc.body;

  // Blocks, in document order. A block detached by an earlier removal (one
  // nested in a losing branch) is skipped.
  for (const block of Array.from(body.querySelectorAll(`[${WHEN_ATTR}]`))) {
    if (!body.contains(block)) continue;
    const condition = block.getAttribute(WHEN_ATTR);
    const sibling = block.nextElementSibling;
    const otherwise =
      sibling && sibling.getAttribute(OTHERWISE_ATTR) === condition
        ? sibling
        : null;

    if (evaluateCondition(condition, values) === true) {
      if (otherwise) otherwise.remove();
      unwrap(block);
    } else {
      block.remove();
      if (otherwise) unwrap(otherwise);
    }
  }

  // Any `otherwise` left is an orphan (its `when` is missing): never render
  // it, since it would show the "off" text unconditionally.
  for (const orphan of Array.from(body.querySelectorAll(`[${OTHERWISE_ATTR}]`))) {
    orphan.remove();
  }

  // Inline values: replace the marker with a TEXT node, the resolved value if
  // known, else the authored fallback text already in the span.
  for (const span of Array.from(body.querySelectorAll(`[${VALUE_ATTR}]`))) {
    const key = span.getAttribute(VALUE_ATTR);
    let text;
    if (VALUE_KEY_RE.test(key) && !RESERVED_WORDS.has(key) && values.has(key)) {
      text = displayValue(key, values.get(key));
    }
    span.replaceWith(doc.createTextNode(text ?? span.textContent));
  }

  return body.innerHTML;
}
